import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { OLITASK_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const TRAVEL_TASK_CATEGORIES_API_BASE_URL = `${OLITASK_SERVICE_BASE_URL}/api/v1/travotask-categories`;
const TRAVEL_CATEGORIES_TOAST_CONFIG = {
  theme: "colored",
  style: { zIndex: 110000 },
};

const normalizeRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const extractErrorMessage = (err, action = "operation") => {
  const fallback = `Failed to ${action}.`;
  const data = err?.response?.data;

  if (!data) {
    if (err?.request) {
      return "No response from server. Please check your internet connection.";
    }
    return err?.message || fallback;
  }

  if (typeof data === "string" && data.trim()) return data;
  if (data?.message) return data.message;
  if (data?.detail) return data.detail;
  if (data?.error) return data.error;

  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors
      .map((entry) => {
        if (typeof entry === "string") return entry;
        const field = entry?.field || entry?.name;
        const message = entry?.defaultMessage || entry?.message || entry?.error;
        if (!message) return null;
        return field ? `${field}: ${message}` : message;
      })
      .filter(Boolean)
      .join("; ");
  }

  return err?.message || fallback;
};

export default function useTravelTaskCategories() {
  const authToken = sessionStorage.getItem("authToken");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAxiosErrors = (err, action = "operation") => {
    const message = extractErrorMessage(err, action);
    const normalizedError = new Error(message);
    normalizedError.status = err?.response?.status;
    normalizedError.raw = err;

    setError(normalizedError);
    toast.error(message, TRAVEL_CATEGORIES_TOAST_CONFIG);
    return normalizedError;
  };

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(TRAVEL_TASK_CATEGORIES_API_BASE_URL);
      setCategories(normalizeRows(response.data));
    } catch (err) {
      handleAxiosErrors(err, "fetch travel categories");
    } finally {
      setLoading(false);
    }
  }, []);

  const createCategory = useCallback(
    async (payload) => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.post(
          TRAVEL_TASK_CATEGORIES_API_BASE_URL,
          payload,
        );
        await fetchCategories();
        toast.success("Travel category created successfully.", TRAVEL_CATEGORIES_TOAST_CONFIG);
        return response.data;
      } catch (err) {
        throw handleAxiosErrors(err, "create travel category");
      } finally {
        setLoading(false);
      }
    },
    [fetchCategories],
  );

  const updateCategory = useCallback(
    async (id, payload) => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.put(
          `${TRAVEL_TASK_CATEGORIES_API_BASE_URL}/${id}`,
          payload,
        );
        await fetchCategories();
        toast.success("Travel category updated successfully.", TRAVEL_CATEGORIES_TOAST_CONFIG);
        return response.data;
      } catch (err) {
        throw handleAxiosErrors(err, "update travel category");
      } finally {
        setLoading(false);
      }
    },
    [fetchCategories],
  );

  const deleteCategory = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        await apiClient.delete(`${TRAVEL_TASK_CATEGORIES_API_BASE_URL}/${id}`);
        await fetchCategories();
        toast.success("Travel category deleted successfully.", TRAVEL_CATEGORIES_TOAST_CONFIG);
      } catch (err) {
        throw handleAxiosErrors(err, "delete travel category");
      } finally {
        setLoading(false);
      }
    },
    [fetchCategories],
  );

  useEffect(() => {
    if (authToken) fetchCategories();
  }, [authToken, fetchCategories]);

  return {
    categories,
    loading,
    error,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
  };
}
